/**
 * Cart Drawer - A comprehensive cart solution for Shopify themes
 * Handles cart drawer functionality, cart updates, and state management
 */

class CartDrawer {
  constructor() {
    this.isOpen = false;
    this.isUpdating = false;
    this.updateTimeouts = new Map();
    this.isHandlingInternalUpdate = false;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCartCount();
  }

  bindEvents() {
    // Cart icon click
    const cartIcon = document.querySelector('.header__icons a[href*="/cart"]');
    if (cartIcon) {
      cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // Close button
    const closeBtn = document.getElementById('cart-drawer-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    const overlay = document.getElementById('cart-drawer-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Checkout button
    const checkoutBtn = document.getElementById('cart-drawer-checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => this.checkout());
    }

    // Listen for cart updates from other parts of the site
    document.addEventListener('cart:updated', (e) => {
      if (!this.isHandlingInternalUpdate) {
        this.handleCartUpdate(e.detail);
      }
    });

    // Listen for add to cart events
    document.addEventListener('cart:item:added', (e) => {
      this.handleItemAdded(e.detail);
    });
  }

  open() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
    
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      drawer.classList.add('active');
    }
    
    this.loadCart();
  }

  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    document.body.style.overflow = '';
    
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      drawer.classList.remove('active');
    }
  }

  async loadCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      this.renderCart(cart);
    } catch (error) {
      console.error('Error loading cart:', error);
      this.showError('Failed to load cart');
    }
  }

  renderCart(cart) {
    const body = document.getElementById('cart-drawer-body');
    const footer = document.getElementById('cart-drawer-footer');
    
    if (!body || !footer) return;

    if (cart.item_count === 0) {
      this.renderEmptyCart(body);
      footer.style.display = 'none';
    } else {
      this.renderCartItems(cart, body);
      this.updateCartTotals(cart);
      footer.style.display = 'block';
    }
  }

  renderEmptyCart(body) {
    body.innerHTML = `
      <div class="cart-drawer-empty">
        <div class="cart-drawer-empty__icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path d="M8 8H12L14 32H50L52 8H56M8 8V56H56V8M8 8L6 16M56 8L58 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="20" cy="56" r="2"/>
            <circle cx="44" cy="56" r="2"/>
          </svg>
        </div>
        <h3 class="cart-drawer-empty__title">Your cart is empty</h3>
        <p class="cart-drawer-empty__message">Looks like you haven't added anything to your cart yet.</p>
        <a href="/collections/all" class="btn-continue-shopping">Continue shopping</a>
      </div>
    `;
  }

  renderCartItems(cart, body) {
    const itemsHtml = cart.items.map(item => this.renderCartItem(item)).join('');
    body.innerHTML = itemsHtml;
    
    // Bind events to the new items
    this.bindCartItemEvents();
  }

  renderCartItem(item) {
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' 
      ? `<div class="cart-drawer-item__variant">${item.variant_title}</div>` 
      : '';
    
    const originalPrice = item.original_line_price > item.final_line_price 
      ? `<span class="cart-drawer-item__price-original">${this.formatMoney(item.original_line_price)}</span>` 
      : '';
    
    return `
      <div class="cart-drawer-item" data-item-key="${item.key}" data-variant-id="${item.variant_id}">
        <div class="cart-drawer-item__image">
          <img src="${item.image}" alt="${item.title}" loading="lazy">
        </div>
        <div class="cart-drawer-item__content-wrapper">
          <div class="cart-drawer-item__details">
            <h4 class="cart-drawer-item__title">
              <a href="${item.url}">${item.title}</a>
            </h4>
            ${variantTitle}
            <div class="cart-drawer-item__price">
              <span class="cart-drawer-item__price-current">${this.formatMoney(item.final_line_price)}</span>
              ${originalPrice}
              <span class="cart-drawer-item__price-unit">(${this.formatMoney(item.final_price)} each)</span>
            </div>
          </div>
        
          <div class="cart-drawer-item__actions">
            <div class="cart-drawer-item__quantity">
              <div class="quantity-controls">
                <button class="btn-quantity btn-quantity-minus" data-action="decrease" data-item-key="${item.key}">-</button>
                <input type="number" value="${item.quantity}" min="1" data-item-key="${item.key}" class="quantity-input">
                <button class="btn-quantity btn-quantity-plus" data-action="increase" data-item-key="${item.key}">+</button>
              </div>
            </div>
            
            <button class="btn-remove-item" data-item-key="${item.key}" data-variant-id="${item.variant_id}">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
            </button>
          </div>
        </div>  
      </div>
    `;
  }

  bindCartItemEvents() {
    // Quantity buttons
    const quantityBtns = document.querySelectorAll('.btn-quantity');
    quantityBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const action = e.target.dataset.action;
        const itemKey = e.target.dataset.itemKey;
        const input = document.querySelector(`input[data-item-key="${itemKey}"]`);
        let quantity = parseInt(input.value);
        
        if (action === 'increase') {
          quantity++;
        } else if (action === 'decrease' && quantity > 1) {
          quantity--;
        }
        
        input.value = quantity;
        this.updateItemQuantity(itemKey, quantity);
      });
    });

    // Quantity inputs
    const quantityInputs = document.querySelectorAll('.quantity-input');
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const itemKey = e.target.dataset.itemKey;
        const quantity = parseInt(e.target.value);
        
        if (quantity < 1) {
          e.target.value = 1;
          return;
        }
        
        this.updateItemQuantity(itemKey, quantity);
      });
    });

    // Remove buttons
    const removeBtns = document.querySelectorAll('.btn-remove-item');
    removeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const itemKey = e.currentTarget.dataset.itemKey;
        console.log(e.currentTarget);
        this.removeItem(itemKey);
      });
    });
  }

  async updateItemQuantity(itemKey, quantity) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    this.showItemLoading(itemKey);
    console.log(itemKey);
    try {
      // Get the variant ID from the DOM element
      const itemElement = document.querySelector(`[data-item-key="${itemKey}"]`);
      const variantId = itemElement?.dataset.variantId;
      console.log(itemElement);
      if (!variantId) {
        throw new Error('Variant ID not found');
      }
      
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        if (response.status === 422) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Quantity limit exceeded');
        }
        throw new Error('Failed to update cart');
      }
      
      const cart = await response.json();
      this.handleCartUpdate(cart);
      
    } catch (error) {
      console.error('Error updating item quantity:', error);
      this.showError(error.message);
      this.revertItemQuantity(itemKey);
    } finally {
      this.isUpdating = false;
      this.hideItemLoading(itemKey);
    }
  }

  async removeItem(itemKey) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    this.showItemLoading(itemKey);
    console.log(itemKey);
    try {
      // Get the variant ID from the DOM element
      const itemElement = document.querySelector(`[data-item-key="${itemKey}"]`);
      const variantId = itemElement?.dataset.variantId;
      
      console.log(itemElement);
      if (!variantId) {
        throw new Error('Variant ID not found');
      }
      
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 0);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const cart = await response.json();
      this.handleCartUpdate(cart);
      
    } catch (error) {
      console.error('Error removing item:', error);
      this.showError('Failed to remove item');
    } finally {
      this.isUpdating = false;
      this.hideItemLoading(itemKey);
    }
  }

  handleCartUpdate(cart) {
    this.renderCart(cart);
    this.updateCartCount();
    this.updateCartTotals(cart);
    
    // Dispatch event for other components (but not for internal updates)
    if (!this.isHandlingInternalUpdate) {
      this.isHandlingInternalUpdate = true;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      this.isHandlingInternalUpdate = false;
    }
  }

  handleItemAdded(item) {
    // Auto-open cart drawer when item is added
    if (!this.isOpen) {
      this.open();
    }
    
    // Refresh cart content
    this.loadCart();
  }

  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('.header__icons a[href*="/cart"] sup, .cart-count-bubble');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
      })
      .catch(error => {
        console.error('Error updating cart count:', error);
      });
  }

  updateCartTotals(cart) {
    const subtotalElement = document.getElementById('cart-drawer-subtotal');
    const totalElement = document.getElementById('cart-drawer-total');
    
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.total_price);
    }
    
    if (totalElement) {
      totalElement.textContent = this.formatMoney(cart.total_price);
    }
  }

  showItemLoading(itemKey) {
    const item = document.querySelector(`[data-item-key="${itemKey}"]`);
    if (item) {
      item.style.opacity = '0.5';
    }
  }

  hideItemLoading(itemKey) {
    const item = document.querySelector(`[data-item-key="${itemKey}"]`);
    if (item) {
      item.style.opacity = '1';
    }
  }

  revertItemQuantity(itemKey) {
    // Reload cart to revert to server state
    this.loadCart();
  }

  showError(message) {
    // Simple error display - could be enhanced with a toast notification
    console.error('Cart Error:', message);
    
    // You could add a toast notification here
    // this.showToast(message, 'error');
  }

  checkout() {
    window.location.href = '/checkout';
  }

  formatMoney(amount) {
    // Use Shopify's money format if available, otherwise fallback to basic formatting
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(amount);
    }
    
    // Fallback to basic currency formatting
    const currency = window.Shopify?.currency?.active || 'USD';
    const symbol = window.Shopify?.currency?.symbol || '$';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount / 100);
  }
}

// Initialize cart drawer when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartDrawer;
}
